import { Button } from "@/components/ui/button";
import { Trash2, Link as LinkIcon, QrCode, Download, FileText, Image as ImageIcon, File, Globe, Lock, Video } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Folder } from "lucide-react";
import { useDiskStore } from "@/store/diskStore";
import { useState, useRef, useEffect } from "react";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface DiskItemProps {
  item: DiskFolder | DiskFile;
  type: "folder" | "file";
  onDoubleClick?: () => void;
}

export default function DiskItem({ item, type, onDoubleClick }: DiskItemProps) {
  const { deleteItem, togglePublic } = useDiskStore();
  const [showQR, setShowQR] = useState(false);
  const qrRef = useRef<HTMLDivElement>(null);
  const qrSvgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (qrRef.current && !qrRef.current.contains(e.target as Node)) {
        setShowQR(false);
      }
    };
    if (showQR) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showQR]);

  const handleDelete = () => {
    if (confirm(`Удалить ${type === "folder" ? "папку" : "файл"} "${item.name}"?`)) {
      const pathForDelete =
        type === "file" && "url" in item
          ? item.url.includes("firebasestorage.googleapis.com")
            ? item.url.split("?")[0].split("/o/")[1].replace("%2F", "/")
            : item.path + "/" + item.name
          : item.path;

      deleteItem(item.id, type, pathForDelete);
    }
  };

  // Публичная ссылка для папки (только если публичная)
  const publicFolderUrl = `${window.location.origin}/public-disk/${item.id}`;

  // Обычная ссылка (приватная или прямая на файл)
  const regularUrl =
    type === "file" && "url" in item
      ? item.url
      : `${window.location.origin}/disk?path=${encodeURIComponent(item.path)}`;

  // Что показываем в QR и копируем
  const qrUrl = type === "folder" && item.isPublic ? publicFolderUrl : regularUrl;

  const getFileIcon = () => {
    if (type === "folder") return <Folder className="h-10 w-10 text-purple-400" />;
    if ("type" in item) {
      if (item.type?.startsWith("image/")) return <ImageIcon className="h-10 w-10 text-pink-400" />;
      if (item.type?.startsWith("video/")) return <Video className="h-10 w-10 text-blue-400" />;
      if (item.type?.includes("pdf") || item.type?.includes("doc")) return <FileText className="h-10 w-10 text-blue-400" />;
    }
    return <File className="h-10 w-10 text-gray-400" />;
  };

  // Копирование QR как чёрно-белого изображения
  const handleCopyQRAsImage = async () => {
    if (!qrSvgRef.current) return;

    const svg = qrSvgRef.current;
    const canvas = document.createElement("canvas");
    const boxSize = 180;
    canvas.width = boxSize;
    canvas.height = boxSize;

    const ctx = canvas.getContext("2d");
    const xml = new XMLSerializer().serializeToString(svg);
    const svg64 = btoa(xml);
    const image = new Image();
    image.src = 'data:image/svg+xml;base64,' + svg64;

    await new Promise((resolve) => (image.onload = resolve));

    ctx?.drawImage(image, 0, 0, boxSize, boxSize);

    canvas.toBlob(async (blob) => {
      if (blob) {
        try {
          await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
          toast({ title: "QR-код скопирован как чёрно-белое изображение" });
        } catch (err) {
          toast({ title: "Ошибка копирования", variant: "destructive" });
        }
      }
    });
  };

  return (
    <div
      className={cn(
        "group relative flex flex-col items-center p-4 rounded-xl transition-all duration-200",
        "hover:bg-purple-800/30 hover:shadow-lg hover:shadow-purple-900/40",
        "border border-purple-700/40 bg-purple-950/20 select-none",
        type === "folder" ? "hover:scale-105 cursor-pointer" : "cursor-default"
      )}
      onDoubleClick={type === "folder" ? onDoubleClick : undefined}
      title={item.name}
    >
      <div className="mb-3 relative">
        {getFileIcon()}
        {item.isPublic && type === "folder" && (
          <Globe className="absolute -top-1 -right-1 h-5 w-5 text-green-400 bg-slate-900 rounded-full p-0.5" />
        )}
      </div>

      <p className="text-center text-sm font-medium text-white truncate w-full max-w-[140px]">
        {item.name}
      </p>

      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {type === "file" && "url" in item && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-purple-300 hover:text-purple-100 hover:bg-purple-700/50"
            onClick={() => window.open(item.url, "_blank")}
          >
            <Download className="h-4 w-4" />
          </Button>
        )}

        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-purple-300 hover:text-purple-100 hover:bg-purple-700/50"
          onClick={() => {
            navigator.clipboard.writeText(qrUrl);
            toast({ title: "Ссылка скопирована" });
          }}
        >
          <LinkIcon className="h-4 w-4" />
        </Button>

        {type === "folder" && (
          <>
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "h-7 w-7",
                item.isPublic
                  ? "text-green-400 hover:text-green-300 hover:bg-green-900/50"
                  : "text-gray-400 hover:text-gray-200 hover:bg-gray-700/50"
              )}
              onClick={() => togglePublic(item.id, type, !item.isPublic)}
            >
              {item.isPublic ? <Globe className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-purple-300 hover:text-purple-100 hover:bg-purple-700/50"
              onClick={() => setShowQR(true)}
              disabled={!item.isPublic} // QR только для публичных папок
            >
              <QrCode className="h-4 w-4" />
            </Button>
          </>
        )}

        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-red-400 hover:text-red-300 hover:bg-red-900/50"
          onClick={handleDelete}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      {showQR && (
        <div
          ref={qrRef}
          className="absolute z-50 right-0 top-full mt-3 bg-slate-900 border border-purple-600 rounded-xl shadow-2xl p-4 w-64"
        >
          {item.isPublic && type === "folder" ? (
            <>
              <div className="bg-white p-3 rounded-lg mx-auto w-fit">
                <QRCodeSVG
                  ref={qrSvgRef}
                  value={publicFolderUrl}
                  size={180}
                  level="Q"
                  fgColor="#000000"  // Чёрный
                  bgColor="#ffffff"  // Белый фон
                />
              </div>
              <p className="text-center text-xs text-purple-300 mt-3">Отсканируй камерой телефона</p>
              <Button
                variant="outline"
                size="sm"
                className="w-full mt-3 border-purple-600 text-purple-300 hover:bg-purple-800/50"
                onClick={handleCopyQRAsImage}
              >
                Скопировать QR как изображение
              </Button>
            </>
          ) : (
            <p className="text-center text-sm text-yellow-300 p-2">
              Сделайте папку публичной, чтобы сгенерировать QR-код
            </p>
          )}

          <Button
            variant="outline"
            size="sm"
            className="w-full mt-3 border-purple-600 text-purple-300 hover:bg-purple-800/50"
            onClick={() => {
              navigator.clipboard.writeText(qrUrl);
              toast({ title: "Ссылка скопирована" });
            }}
          >
            Скопировать ссылку
          </Button>
        </div>
      )}
    </div>
  );
}